import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useFeds } from '@/api/hooks';
import { LIBELLE_NATURE, LIBELLE_STATUT, type StatutDocument } from '@/api/types';
import { peutEmettreDocuments, peutEmettreFedAchats } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, PaiementBadge, StatutBadge, Vide } from '@/components/common';
import { BandeauxACorriger, EntonnoirDocuments, niveauDocument, type CleNiveau } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFiltresListe } from '@/lib/filtres-liste';
import { dateFr, montant } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT) as StatutDocument[];

export function FedListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const valideur = u.etapes.length > 0;
  const tresorier = u.profils.some((p) => p === 'TRESORERIE' || p === 'CHEF_COMPTABLE' || p === 'DIRECTEUR_FINANCIER');
  const peutEmettre = peutEmettreDocuments(u.habilitations) || peutEmettreFedAchats(u.habilitations);
  const onglets = [...(valideur ? (['a-traiter'] as const) : []), ...(tresorier ? (['a-payer'] as const) : []), 'tous' as const];
  const { onglet, statut, setOnglet, setStatut } = useFiltresListe(onglets, valideur ? 'a-traiter' : tresorier ? 'a-payer' : 'tous', 'tous');
  const filtre = onglet === 'a-traiter' ? { aTraiter: true } : onglet === 'a-payer' ? { statut: 'AUTORISE' as const } : statut === 'TOUS' ? {} : { statut };
  const { data, isLoading, error } = useFeds(filtre);
  const liste = onglet === 'a-payer' ? (data ?? []).filter((f) => f.etatPaiement !== 'PAYE') : (data ?? []);
  // « Où en sont mes FED ? » (maquette émetteur) : résumé par niveau du circuit, filtre au clic, bandeaux « à corriger ».
  const [niveau, setNiveau] = useState<CleNiveau | null>(null);
  const miens = (data ?? []).filter((f) => f.emetteur?.id === u.agentId);
  const resume = miens.map((f) => ({ id: f.id, numero: f.numero, titre: f.objet, statut: f.statut, etapeCourante: f.etapeCourante, montant: f.montantUsd, correction: f.correction }));
  const visibles = niveau ? liste.filter((f) => niveauDocument(f) === niveau) : liste;

  return (
    <div>
      <PageHeader
        titre="Formulaires d'engagement des dépenses"
        sousTitre="Un FED engage le budget à son autorisation ; la trésorerie le met en paiement puis le paie."
        actions={
          peutEmettre && (
            <Button asChild>
              <Link to="/fed/nouveau">
                <Plus className="size-4" /> Nouveau FED
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as typeof onglet)}>
          <TabsList>
            {valideur && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
            {tresorier && <TabsTrigger value="a-payer">À payer</TabsTrigger>}
            <TabsTrigger value="tous">{u.habilitations.some((h) => h.structureId === null) ? 'Tous les FED' : 'Mes FED et ceux de ma structure'}</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet === 'tous' && (
          <Select value={statut} onValueChange={(v) => setStatut(v as StatutDocument | 'TOUS')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOUS">Tous les statuts</SelectItem>
              {STATUTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {LIBELLE_STATUT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {peutEmettre && onglet === 'tous' && statut === 'TOUS' && resume.length > 0 && (
        <div className="mb-4 space-y-3">
          <BandeauxACorriger documents={resume} chemin={(x) => `/fed/${x}`} libelle="FED" />
          <EntonnoirDocuments libelle="FED" documents={resume} etapes={['DIRECTEUR', 'COORDONNATEUR_MEMORIAL', 'CONTROLE_GESTION', 'DIRECTEUR_FINANCIER', 'DGA_FINANCES', 'DIRECTEUR_GENERAL']} selection={niveau} onSelection={setNiveau} />
        </div>
      )}
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !visibles.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucun FED en attente de votre décision.' : onglet === 'a-payer' ? 'Aucun FED autorisé en attente de paiement.' : 'Aucun FED. Un FED se crée depuis un TDR autorisé.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead className="hidden md:table-cell">Nature</TableHead>
                <TableHead className="hidden lg:table-cell">TDR</TableHead>
                <TableHead className="text-right">Montant (USD)</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <Link to={`/fed/${f.id}`} className="font-medium text-primary hover:underline">
                      {f.numero ?? 'Brouillon'}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[36ch] truncate">{f.objet}</TableCell>
                  <TableCell className="hidden md:table-cell">{LIBELLE_NATURE[f.nature]}</TableCell>
                  <TableCell className="hidden lg:table-cell">{f.tdr?.numero ?? '—'}</TableCell>
                  <TableCell className="num text-right">{montant(f.montantUsd, true)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={f.statut} />
                    {f.statut === 'SOUMIS' && <EtapeBadge etape={f.etapeCourante} />}
                    {f.correction && <span className="text-xs text-amber-700">avant le {dateFr(f.correction.dateLimite)}</span>}
                    {f.annulationAutomatique && <span className="text-xs text-muted-foreground">sans réponse</span>}
                    {f.statut === 'AUTORISE' && <PaiementBadge etat={f.etatPaiement} />}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(f.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
